import { formatCurrencyAmountWithIssue } from '../../../../../commons/currency/Currency.Functions';
import React, { useMemo } from 'react';
import {
  Badge,
  Button,
  Divider,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
  Text
} from '@fluentui/react-components';
import {
  ArrowTrendingDownFilled,
  ArrowTrendingFilled,
  Dismiss24Regular
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { Concept } from '../../../../../models/Entities/Contracts/Concept';
import { ConceptCostHistory } from '../../../../../models/Entities/Contracts/ConceptCostHistory';
import { useConceptSponsorDetailsStyles } from './ConceptSponsorDetails.Styles';
import ConceptValueHistoryChartComponent from '../ConceptValueHistoryChart/ConceptValueHistoryChart.Component';

export interface ConceptSponsorDetailsProps {
  concept: Concept | undefined;
  isOpen: boolean;
  onDismiss: () => void;
}

const formatCost = (value: number | null | undefined, currency: string | null | undefined): string =>
  typeof value === 'number' && Number.isFinite(value)
    ? formatCurrencyAmountWithIssue(value, currency)
    : '-';

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

interface VarianceBadgeProps {
  value: number;
}

const VarianceBadge: React.FC<VarianceBadgeProps> = ({ value }) => {
  if (!value || value === 0) return null;
  return (
    <Badge
      appearance="tint"
      color={value > 0 ? 'success' : 'danger'}
      size="small"
      icon={value > 0 ? <ArrowTrendingFilled /> : <ArrowTrendingDownFilled />}
    >
      {value > 0 ? '+' : ''}{value.toFixed(1)}%
    </Badge>
  );
};

interface CostRowProps {
  label: string;
  previous: number;
  next: number;
  variance: number;
  currency: string;
  styles: ReturnType<typeof useConceptSponsorDetailsStyles>;
}

const CostRow: React.FC<CostRowProps> = ({ label, previous, next, variance, currency, styles }) => (
  <div className={styles.historyCostField}>
    <Text size={100} className={styles.fieldLabel}>{label}</Text>
    <div className={styles.historyArrow}>
      <Text size={200} strikethrough style={{ color: 'var(--colorNeutralForeground4)' }}>
        {formatCost(previous, currency)}
      </Text>
      <Text size={100} style={{ color: 'var(--colorNeutralForeground3)' }}>→</Text>
      <Text size={200} weight="semibold">{formatCost(next, currency)}</Text>
      <VarianceBadge value={variance} />
    </div>
  </div>
);

const ConceptSponsorDetailsComponent: React.FC<ConceptSponsorDetailsProps> = ({ concept, isOpen, onDismiss }) => {
  const { t } = useTranslation();
  const styles = useConceptSponsorDetailsStyles();

  const sortedHistories: ConceptCostHistory[] = useMemo(
    () =>
      (concept?.conceptCostHistories || [])
        .slice()
        .sort((a, b) => new Date(b.changeDate).getTime() - new Date(a.changeDate).getTime()),
    [concept?.conceptCostHistories]
  );

  const currency = concept?.costCurrency?.trim() || 'ARS';

  return (
    <OverlayDrawer open={isOpen} position="end" size="medium" onOpenChange={(_, { open }) => !open && onDismiss()}>
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button appearance="subtle" icon={<Dismiss24Regular />} onClick={onDismiss} />
          }
        >
          {concept?.conceptName || t('conceptSponsorDetails.title', 'Detalle del concepto')}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className="ScrollShadow">
        <div className={styles.body}>

          {/* General info */}
          <section className={styles.section}>
            <Text size={200} weight="semibold" className={styles.sectionTitle}>
              {t('conceptSponsorDetails.sections.general', 'Información general')}
            </Text>
            <div className={styles.fieldGrid}>
              <div className={styles.field}>
                <Text size={200} className={styles.fieldLabel}>{t('conceptSponsorDetails.fields.code', 'Código')}</Text>
                <Text size={300} weight="semibold">{concept?.conceptCode || '-'}</Text>
              </div>
              <div className={styles.field}>
                <Text size={200} className={styles.fieldLabel}>{t('conceptSponsorDetails.fields.referenceId', 'ID Referencia')}</Text>
                <Text size={300}>{concept?.conceptReferenceId || '-'}</Text>
              </div>
              {concept?.conceptDescription && (
                <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                  <Text size={200} className={styles.fieldLabel}>{t('conceptSponsorDetails.fields.description', 'Descripción')}</Text>
                  <Text size={300}>{concept.conceptDescription}</Text>
                </div>
              )}
            </div>
          </section>

          <Divider />

          {/* Current costs */}
          <section className={styles.section}>
            <Text size={200} weight="semibold" className={styles.sectionTitle}>
              {t('conceptSponsorDetails.sections.costs', 'Costos actuales')}
            </Text>
            <div className={styles.fieldGrid}>
              <div className={styles.field}>
                <Text size={200} className={styles.fieldLabel}>{t('conceptSponsorDetails.fields.referenceCost', 'Costo referencia')}</Text>
                <div className={styles.fieldValue}>
                  <Text size={300} className={styles.costValue}>{formatCost(concept?.referenceCost, currency)}</Text>
                  {sortedHistories.length > 0 && (
                    <VarianceBadge
                      value={sortedHistories.find(h => h.referenceCostVariancePercentage !== 0)?.referenceCostVariancePercentage ?? 0}
                    />
                  )}
                </div>
              </div>
              <div className={styles.field}>
                <Text size={200} className={styles.fieldLabel}>{t('conceptSponsorDetails.fields.sponsorCost', 'Costo sponsor')}</Text>
                <div className={styles.fieldValue}>
                  <Text size={300} className={styles.costValue}>{formatCost(concept?.sponsorWeigthedCost, currency)}</Text>
                  {sortedHistories.length > 0 && (
                    <VarianceBadge
                      value={sortedHistories.find(h => h.sponsorWeigthedCostVariancePercentage !== 0)?.sponsorWeigthedCostVariancePercentage ?? 0}
                    />
                  )}
                </div>
              </div>
              <div className={styles.field}>
                <Text size={200} className={styles.fieldLabel}>{t('conceptSponsorDetails.fields.siteCost', 'Costo sitio')}</Text>
                <div className={styles.fieldValue}>
                  <Text size={300} className={styles.costValue}>{formatCost(concept?.siteWeigthedCost, currency)}</Text>
                  {sortedHistories.length > 0 && (
                    <VarianceBadge
                      value={sortedHistories.find(h => h.siteWeigthedCostVariancePercentage !== 0)?.siteWeigthedCostVariancePercentage ?? 0}
                    />
                  )}
                </div>
              </div>
              <div className={styles.field}>
                <Text size={200} className={styles.fieldLabel}>{t('conceptSponsorDetails.fields.countryCost', 'Costo país')}</Text>
                <div className={styles.fieldValue}>
                  <Text size={300} className={styles.costValue}>{formatCost(concept?.countryWeigthedCost, currency)}</Text>
                  {sortedHistories.length > 0 && (
                    <VarianceBadge
                      value={sortedHistories.find(h => h.countryWeigthedCostVariancePercentage !== 0)?.countryWeigthedCostVariancePercentage ?? 0}
                    />
                  )}
                </div>
              </div>
              <div className={styles.field}>
                <Text size={200} className={styles.fieldLabel}>{t('conceptSponsorDetails.fields.globalCost', 'Costo global')}</Text>
                <div className={styles.fieldValue}>
                  <Text size={300} className={styles.costValue}>{formatCost(concept?.globalWeigthedCost, currency)}</Text>
                  {sortedHistories.length > 0 && (
                    <VarianceBadge
                      value={sortedHistories.find(h => h.globalWeigthedCostVariancePercentage !== 0)?.globalWeigthedCostVariancePercentage ?? 0}
                    />
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Reference cost chart */}
          {sortedHistories.length > 0 && (
            <>
              <Divider />
              <section className={styles.section}>
                <Text size={200} weight="semibold" className={styles.sectionTitle}>
                  {t('conceptSponsorDetails.sections.referenceCostChart', 'Evolución del costo de referencia')}
                </Text>
                <ConceptValueHistoryChartComponent
                  histories={concept?.conceptCostHistories || []}
                  currency={currency}
                />
              </section>
            </>
          )}

          {/* Cost history */}
          {sortedHistories.length > 0 && (
            <>
              <Divider />
              <section className={styles.section}>
                <Text size={200} weight="semibold" className={styles.sectionTitle}>
                  {t('conceptSponsorDetails.sections.history', 'Historial de cambios')}
                </Text>
                <div className={styles.historyList}>
                  {sortedHistories.map((entry) => (
                    <div key={entry.id} className={styles.historyItem}>
                      <Text size={200} weight="semibold" className={styles.historyDate}>
                        {formatDate(entry.changeDate)}
                      </Text>
                      <div className={styles.historyCosts}>
                        {entry.referenceCostVariancePercentage !== 0 && (
                          <CostRow
                            label={t('conceptSponsorDetails.fields.referenceCost', 'Costo referencia')}
                            previous={entry.previousReferenceCost}
                            next={entry.newReferenceCost}
                            variance={entry.referenceCostVariancePercentage}
                            currency={currency}
                            styles={styles}
                          />
                        )}
                        {entry.sponsorWeigthedCostVariancePercentage !== 0 && (
                          <CostRow
                            label={t('conceptSponsorDetails.fields.sponsorCost', 'Costo sponsor')}
                            previous={entry.previousSponsorWeigthedCost}
                            next={entry.newSponsorWeigthedCost}
                            variance={entry.sponsorWeigthedCostVariancePercentage}
                            currency={currency}
                            styles={styles}
                          />
                        )}
                        {entry.siteWeigthedCostVariancePercentage !== 0 && (
                          <CostRow
                            label={t('conceptSponsorDetails.fields.siteCost', 'Costo sitio')}
                            previous={entry.previousSiteWeigthedCost}
                            next={entry.newSiteWeigthedCost}
                            variance={entry.siteWeigthedCostVariancePercentage}
                            currency={currency}
                            styles={styles}
                          />
                        )}
                        {entry.countryWeigthedCostVariancePercentage !== 0 && (
                          <CostRow
                            label={t('conceptSponsorDetails.fields.countryCost', 'Costo país')}
                            previous={entry.previousCountryWeigthedCost}
                            next={entry.newCountryWeigthedCost}
                            variance={entry.countryWeigthedCostVariancePercentage}
                            currency={currency}
                            styles={styles}
                          />
                        )}
                        {entry.globalWeigthedCostVariancePercentage !== 0 && (
                          <CostRow
                            label={t('conceptSponsorDetails.fields.globalCost', 'Costo global')}
                            previous={entry.previousGlobalWeigthedCost}
                            next={entry.newGlobalWeigthedCost}
                            variance={entry.globalWeigthedCostVariancePercentage}
                            currency={currency}
                            styles={styles}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}

        </div>
      </DrawerBody>

      <DrawerFooter>
        <Button appearance="secondary" onClick={onDismiss}>
          {t('common.close', 'Cerrar')}
        </Button>
      </DrawerFooter>
    </OverlayDrawer>
  );
};

export default ConceptSponsorDetailsComponent;
